import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { PlaceCard } from '../components/PlaceCard';
import { FilterBar } from '../components/FilterBar';
import { SearchBar } from '../components/SearchBar';
import { Footer } from '../components/Footer';
import { ScrollToTop } from '../components/ScrollToTop';
import { places, categories } from '../data/places';
import type { PlaceCategory } from '../data/places';
export function AllPlacesPage() {
  const [selectedCategories, setSelectedCategories] = useState<PlaceCategory[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [displayCount, setDisplayCount] = useState(20);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleCategoryToggle = (category: PlaceCategory) => {
    setSelectedCategories(prev => prev.includes(category) ? prev.filter(c => c !== category) : [...prev, category]);
  };
  const handleClearFilters = () => {
    setSelectedCategories([]);
    setSearchQuery('');
  };
  const filteredAndSortedPlaces = useMemo(() => {
    let filtered = places;
    if (selectedCategories.length > 0) {
      filtered = filtered.filter(place => place.categories.some(cat => selectedCategories.includes(cat)));
    }
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(place => place.name.toLowerCase().includes(query) || place.description.toLowerCase().includes(query) || place.location.toLowerCase().includes(query) || place.categories.some(cat => cat.toLowerCase().includes(query)));
    }
    return [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedCategories, searchQuery]);
  const displayedPlaces = filteredAndSortedPlaces.slice(0, displayCount);
  const hasMore = displayCount < filteredAndSortedPlaces.length;
  return <div className="min-h-screen bg-white" data-id="element-375">
      {/* Hero */}
      <div className="border-b border-gray-200" data-id="element-376">
        <div className="max-w-7xl mx-auto px-8 lg:px-12 py-20 md:py-32" data-id="element-377">
          <motion.div initial={{
          y: 20,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} transition={{
          duration: 0.6
        }} className="max-w-4xl" data-id="element-378">
            <div className="h-px w-16 bg-amber-500 mb-8" data-id="element-379"></div>
            <h1 className="text-7xl md:text-9xl font-light text-gray-900 mb-6 leading-[0.9] tracking-tight" data-id="element-380">
              All
              <br data-id="element-381" />
              <span className="font-serif italic" data-id="element-382">Places</span>
            </h1>
            <p className="text-2xl md:text-3xl text-gray-600 leading-relaxed font-light max-w-2xl" data-id="element-383">
              Explore every curated destination in Nairobi, from wildlife
              sanctuaries to cultural landmarks.
            </p>
          </motion.div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white border-b border-gray-200" data-id="element-384">
        <div className="max-w-7xl mx-auto px-8 lg:px-12 py-8" data-id="element-385">
          <div className="max-w-2xl" data-id="element-386">
            <SearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search destinations..." data-id="element-387" />
          </div>
        </div>
      </div>

      {/* Filter Section */}
      <div className="bg-gray-50 border-b border-gray-200" data-id="element-388">
        <div className="max-w-7xl mx-auto px-8 lg:px-12 py-12" data-id="element-389">
          <motion.div initial={{
          opacity: 0,
          y: 10
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2
        }} className="mb-8" data-id="element-390">
            <div className="h-px w-16 bg-amber-500 mb-6" data-id="element-391"></div>
            <h2 className="text-3xl font-light text-gray-900 mb-3 tracking-tight" data-id="element-392">
              Filter by Category
            </h2>
            <p className="text-gray-600 font-light" data-id="element-393">
              Select your preferred experiences
            </p>
          </motion.div>
        </div>
      </div>

      <FilterBar selectedCategories={selectedCategories} onCategoryToggle={handleCategoryToggle} onClearFilters={handleClearFilters} data-id="element-394" />

      {/* Results Count */}
      <div className="max-w-7xl mx-auto px-8 lg:px-12 mt-12" data-id="element-395">
        <motion.p className="text-sm text-gray-500 font-light tracking-wider uppercase" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} key={filteredAndSortedPlaces.length} data-id="element-396">
          {filteredAndSortedPlaces.length}{' '}
          {filteredAndSortedPlaces.length === 1 ? 'Destination' : 'Destinations'}
          {selectedCategories.length > 0 && <span data-id="element-397"> · {selectedCategories.join(', ')}</span>}
        </motion.p>
      </div>

      {/* Places Grid - 4 Columns */}
      <div className="max-w-7xl mx-auto px-8 lg:px-12 py-12" data-id="element-398">
        {filteredAndSortedPlaces.length > 0 ? <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12" data-id="element-399">
              {displayedPlaces.map((place, index) => <PlaceCard key={place.id} place={place} index={index} data-id="element-400" />)}
            </div>

            {/* Load More Button */}
            {hasMore && <div className="text-center mb-20" data-id="element-401">
                <button onClick={() => setDisplayCount(prev => prev + 20)} className="px-10 py-4 border-2 border-gray-900 text-gray-900 font-light hover:bg-gray-900 hover:text-white transition-colors text-sm uppercase tracking-widest" data-id="element-402">
                  Load More Destinations
                </button>
              </div>}
          </> : <motion.div className="text-center py-32 bg-white border border-gray-200" initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} data-id="element-403">
            <p className="text-4xl font-light text-gray-400 mb-6" data-id="element-404">
              No destinations found
            </p>
            <p className="text-gray-600 mb-8 font-light" data-id="element-405">
              Refine your search criteria
            </p>
            <button onClick={handleClearFilters} className="px-8 py-3 border border-gray-900 text-gray-900 font-light hover:bg-gray-900 hover:text-white transition-colors text-sm uppercase tracking-widest" data-id="element-406">
              Clear Filters
            </button>
          </motion.div>}
      </div>

      <ScrollToTop data-id="element-407" />
      <Footer data-id="element-408" />
    </div>;
}